import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { FiChevronDown } from 'react-icons/fi';

import api from '../../services/api';
import { useFeature } from '../../hooks/features';

function formatDays(value) {
  return Number(value).toLocaleString('en-US', { maximumFractionDigits: 2 });
}

const cell = { border: '1px solid rgba(224, 224, 224, 1)', whiteSpace: 'nowrap' };

// Semanas da grade: da primeira à última que aparecem no time daquela ordem,
// sem buracos (semana sem dias fica vazia, como na alocação).
function weekColumns(team) {
  const all = team.flatMap(member => member.Weeks.map(w => w.Week));
  if (all.length === 0) return [];
  const first = Math.min(...all);
  const last = Math.max(...all);
  return Array.from({ length: last - first + 1 }, (_, i) => first + i);
}

// Grade de uma ordem, no formato da alocação: posição, notas (workfront) e dias
// por semana. É gabarito: semanas relativas (W1, W2…), sem datas — as datas reais
// estão na alocação do projeto.
function TeamGrid({ order }) {
  const weeks = weekColumns(order.Team);

  return (
    <div style={{ overflowX: 'auto' }}>
      <Table size="small" style={{ borderCollapse: 'collapse' }}>
        <TableHead>
          <TableRow>
            <TableCell style={{ ...cell, fontWeight: 600, minWidth: 120 }}>
              Professional
            </TableCell>
            <TableCell style={{ ...cell, fontWeight: 600, minWidth: 80 }}>Notes</TableCell>
            {weeks.map(week => (
              <TableCell key={week} align="center" style={{ ...cell, fontWeight: 600 }}>
                W{week}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {order.Team.map(member => {
            const days = new Map(member.Weeks.map(w => [w.Week, w.Days]));
            return (
              <TableRow key={member.ID}>
                <TableCell style={cell}>{member.PositionName || '-'}</TableCell>
                <TableCell style={{ ...cell, whiteSpace: 'normal' }}>
                  {member.Workfront || ''}
                </TableCell>
                {weeks.map(week => (
                  <TableCell key={week} align="center" style={cell}>
                    {days.has(week) && days.get(week) > 0 ? formatDays(days.get(week)) : ''}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

// Espelho do time vendido no inclient (só leitura): uma grade por ordem do projeto —
// a criação e cada extensão anexada. Orienta quem escala; não recomenda ninguém.
export default function SoldTeam({ projectId }) {
  const { enabled } = useFeature('projectOrders');
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (!enabled || !projectId) return;
    api
      .get('scheduling/project-orders/team', { params: { projectId } })
      .then(response => setOrders(response.data || []))
      .catch(() => setOrders([]));
  }, [enabled, projectId]);

  if (!enabled || orders.length === 0) return null;

  return (
    <Accordion style={{ marginTop: 24 }}>
      <AccordionSummary expandIcon={<FiChevronDown />}>
        <Typography style={{ fontWeight: 500 }}>Sold team (from inclient)</Typography>
      </AccordionSummary>
      <AccordionDetails style={{ display: 'block', padding: '0 0 8px' }}>
        {orders.map(order => (
          <div key={order.ID} style={{ marginBottom: 16 }}>
            {/* Com extensões anexadas, cada grade diz de qual venda é. */}
            {orders.length > 1 && (
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ padding: '0 16px 6px' }}
              >
                {order.IsExtension ? 'Extension' : 'New project'}
              </Typography>
            )}
            <TeamGrid order={order} />
          </div>
        ))}
      </AccordionDetails>
    </Accordion>
  );
}
